import React, { forwardRef } from 'react';
import { FormControl, FormHelperText, FormLabel, InputLabel } from '@mui/material';

import type { FieldBaseProps, FieldIds } from './field.types';
import { hiddenRequiredMarkSx, staticLabelSx, visuallyHiddenSx } from './fieldStyles';

export type FieldShellKind = 'control' | 'group' | 'radiogroup';

interface FieldShellCommonProps extends Omit<FieldBaseProps, 'describedById'> {
  ids: FieldIds;
  children: React.ReactNode;
}

export type FieldShellProps = FieldShellCommonProps &
  ({ kind?: Exclude<FieldShellKind, 'group'> } | { kind: 'group'; describedById?: string });

const helperSx = { mx: 0 } as const;
const shellSx = { minWidth: 0, border: 0, p: 0, m: 0 } as const;

export const hasHelperText = (helperText: React.ReactNode): boolean =>
  helperText !== undefined && helperText !== null && typeof helperText !== 'boolean' && helperText !== '';

const hasLabel = (label: React.ReactNode): boolean => label !== undefined;

export const describedBy = (
  ids: FieldIds,
  helperText: React.ReactNode,
  ...extraIds: ReadonlyArray<string | undefined>
): string | undefined => {
  const parts = [hasHelperText(helperText) ? ids.helperId : undefined, ...extraIds].filter(
    (part): part is string => Boolean(part),
  );
  return parts.length === 0 ? undefined : parts.join(' ');
};

export const labelledBy = (ids: FieldIds, label: React.ReactNode): string | undefined =>
  hasLabel(label) ? ids.labelId : undefined;

export const FieldHelperText = ({ ids, helperText }: { ids: FieldIds; helperText: React.ReactNode }) => {
  if (!hasHelperText(helperText)) return null;
  return (
    <FormHelperText id={ids.helperId} sx={helperSx}>
      {helperText}
    </FormHelperText>
  );
};

interface FieldLabelProps {
  kind: FieldShellKind;
  ids: FieldIds;
  hideLabel?: boolean;
  children: React.ReactNode;
}

const FieldLabel = ({ kind, ids, hideLabel, children }: FieldLabelProps) => {
  const sx = hideLabel ? visuallyHiddenSx : staticLabelSx;
  if (kind === 'group') {
    return (
      <FormLabel component="legend" id={ids.labelId} sx={sx}>
        {children}
      </FormLabel>
    );
  }
  if (kind === 'radiogroup') {
    return (
      <FormLabel id={ids.labelId} sx={sx}>
        {children}
      </FormLabel>
    );
  }
  return (
    <InputLabel id={ids.labelId} htmlFor={ids.id} shrink disableAnimation sx={sx}>
      {children}
    </InputLabel>
  );
};

export const FieldShell = forwardRef<HTMLElement, FieldShellProps>(function FieldShell(props, ref) {
  const { ids, children, kind = 'control', label, hideLabel, helperText, error, required, hideRequiredMark = false, disabled, size = 'small', fullWidth = true, className, sx } = props;
  const groupDescription = props.kind === 'group' ? describedBy(ids, helperText, props.describedById) : undefined;
  return (
    <FormControl
      ref={ref as React.Ref<HTMLDivElement>}
      component={kind === 'group' ? 'fieldset' : 'div'}
      aria-describedby={groupDescription}
      variant="outlined"
      size={size}
      error={error}
      disabled={disabled}
      required={required}
      fullWidth={fullWidth}
      className={className}
      sx={[shellSx, hideRequiredMark && hiddenRequiredMarkSx, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {hasLabel(label) && (
        <FieldLabel kind={kind} ids={ids} hideLabel={hideLabel}>
          {label}
        </FieldLabel>
      )}
      {children}
      <FieldHelperText ids={ids} helperText={helperText} />
    </FormControl>
  );
});
